import Layout from "../../../components/layout";
import { SEO } from "../../../components/seo";
import { Header } from "../../../components/header";
import { H2, H3, Text } from "../../../components/text";
import { Card } from "../../../components/card";
import { FullWidth } from "../../../components/full-width";
import { Hero } from "../../../components/hero";
import { LinkButton } from "../../../components/link-button";
import { Row } from "../../../components/row";
import { Strapline } from "../../../components/strapline";
import { graphql } from "gatsby";

export const pageQuery = graphql`
  query {
    heroImage: file(relativePath: { eq: "hero/wildlife-appeal.jpg" }) {
      ...heroImageData
    }
    fullWidthImage: file(relativePath: { eq: "card/wildlife-appeal-3.jpg" }) {
      ...fullWidthImageData
    }
    cardImage1: file(relativePath: { eq: "card/wildlife-appeal-1.jpg" }) {
      ...cardImageData
    }
    cardImage2: file(relativePath: { eq: "card/wildlife-appeal-2.jpg" }) {
      ...cardImageData
    }
    cardImage3: file(
      relativePath: {
        eq: "card/giving-our-biggest-patients-a-brighter-future.jpg"
      }
    ) {
      ...cardImageData
    }
    cardImage4: file(
      relativePath: { eq: "card/dealing-with-the-unexpected.jpg" }
    ) {
      ...cardImageData
    }
    cardImage5: file(
      relativePath: { eq: "card/working-together-for-wildlife.jpg" }
    ) {
      ...cardImageData
    }
    donateCardImage: file(relativePath: { eq: "card/dog-2.jpg" }) {
      ...cardImageData
    }
  }
`;

const WildlifeAppealPage = ({ data }) => (
  <Layout>
    <SEO
      description="You have the power to protect wildlife. Donate today and you can help provide expert veterinary care to wildlife around the world, from treating injured birds of prey in India to caring for sick and orphaned elephants in Myanmar."
      title="Conservation through Vet Care"
      meta={[
        {
          name: `robots`,
          content: `noindex`,
        },
      ]}
    />
    <Header showDonateBar />
    <Hero
      colour="secondary"
      height="300px"
      title="Conservation through Vet Care"
      image={data.heroImage}
    />
    <Strapline
      colour="primary"
      title="Nature is amazing. From the smallest insect to the largest mammal, everything has its place. But, too often, we see the delicate balance of nature disrupted. Small actions can cause devastating consequences, with our wildlife paying the price."
    />
    <Row className="mt-50 mb-50">
      <Card image={data.cardImage1}>
        <H3>You have the power to protect wildlife</H3>
        <Text>
          Donate today and you can help provide expert veterinary care to
          wildlife around the world, from treating injured birds of prey in
          India to caring for sick and orphaned elephants in Myanmar.
        </Text>
        <Text>
          Veterinary care does far more than treat injuries and disease, it
          prevents ecosystems being disrupted, diseases being introduced and
          stops habitats becoming overwhelmed and overpopulated. Our latest
          project in the Galapagos does just that, protecting wildlife by
          providing expert care to domestic pets on the island.
        </Text>
        <Text>
          <strong>
            Find out how your support can protect wildlife around the world...
          </strong>
        </Text>
        <div className="d-flex flex-r mt-30">
          <LinkButton title="Donate" to="/support-us/wildlife-appeal/amount">
            Donate
          </LinkButton>
        </div>
        <span id="galapagos" />
      </Card>
    </Row>
    <Row>
      <Card image={data.cardImage2} imageFirst>
        <H3>Wildlife, pets and people in the Galapagos</H3>
        <Text>
          Known for their diverse wildlife and unique habitats, the Galapagos
          Islands are an awe-inspiring display of the beauty of the natural
          world. From tortoises to seals, iguanas to flamingos, all types of
          wildlife live side by side.
        </Text>
        <Text>
          <strong>But, now they face another threat.</strong>
        </Text>
        <Text>
          Despite strict laws on breeding and importing domestic pets, the
          numbers of cats and dogs on the islands have dramatically increased.
          The impact of Covid-19 has made this even worse, with loss of income
          from tourism causing many people to return to the mainland, and many
          having to leave their pets behind.
        </Text>
        <Text>
          As these dogs and cats breed, they will often roam further afield,
          unwittingly threatening the delicate ecosystems they live amongst. As
          their numbers increase they risk encroaching on the endemic wildlife’s
          habitat, and competing with them for resources, damaging or disrupting
          food chains. They can also bring diseases and parasites that the
          endemic wildlife has no immunity to, such as Canine Distemper Virus
          (CDV). As the numbers of dogs and cats increase, so does the risk to
          the unique and delicate Galapagos Wildlife.
        </Text>
        <Text>
          Sadly, it is not just the native wildlife that is suffering, the dogs
          and cats on the island need support and further access to veterinary
          care, as many are suffering from preventable conditions such as Tick
          Fever and Canine Parovirus.
        </Text>
        <Text>
          <strong>
            With your help we can change this. Our expert vets have now arrived
            in the Galapagos, ready to work with the government and local
            community to help change the lives of pets and wildlife.
          </strong>
        </Text>
        <Text>
          <strong>
            Donate today, and you can help restore balance for wildlife in the
            Galapagos, and give much needed care to the cats and dogs that live
            there.
          </strong>
        </Text>
        <div className="d-flex flex-r mt-30">
          <LinkButton title="Donate" to="/support-us/wildlife-appeal/amount">
            Donate
          </LinkButton>
        </div>
      </Card>
    </Row>
    <div className="mt-50">
      <FullWidth image={data.fullWidthImage} colour="secondary">
        <div>
          <H3>Your support will</H3>
          <ul>
            <li>
              help control the numbers of cats and dogs on the island through
              humane neutering programs, not only protecting wildlife, but also
              keeping cat and dog populations healthy
            </li>
            <li>
              provide accessible veterinary healthcare for domestic pets on the
              islands
            </li>
            <li>
              educate the local communities on animal welfare and managing
              populations to ensure this is a long-term solution
            </li>
          </ul>
          <Text>
            <strong>
              With your help wildlife, pets and people can live together in
              harmony, on these beautiful islands.
            </strong>
          </Text>
          <div className="d-flex flex-r mt-30">
            <LinkButton title="Donate" to="/support-us/wildlife-appeal/amount">
              Donate
            </LinkButton>
          </div>
        </div>
      </FullWidth>
    </div>
    <Row className="mt-50 mb-20 text-center">
      <H2>
        The Galapagos is our newest project, but it is not the only way we are
        helping wildlife around the world...
      </H2>
      <span id="brighter-future" />
    </Row>
    <Row className="mb-50">
      <Card image={data.cardImage3}>
        <H3>Giving our biggest patients a brighter future</H3>
        <Text>
          Elephants may be our largest land mammals, but this does not stop them
          from being exploited or abused. Whether it is losing their habitat,
          being poached for their ivory or simply not receiving veterinary care,
          they have many challenges to face.
        </Text>
        <Text>
          <strong>
            Together we can give them a better future. Donate today to make a
            difference for vulnerable elephants.
          </strong>
        </Text>
        <Text>
          We are working in Myanmar, home to the world’s largest population of
          captive elephants, at the country’s only elephant hospital. Your
          support can help provide the hospital with veterinary supplies,
          ongoing training and expert care for their larger than average
          patients.
        </Text>
        <span id="unexpected" />
      </Card>
    </Row>
    <Row className="mb-50">
      <Card image={data.cardImage4} imageFirst>
        <H3>Dealing with the unexpected</H3>
        <Text>
          Our expert vets around the world deal with veterinary emergencies and
          save lives every day. But even they can’t predict the types of animals
          they will need their help.
        </Text>
        <Text>
          Our team in Malawi recently treated a Steppe Buzzard that was weak and
          unable to fly after being attacked by crows. They examined this
          beautiful bird, treated its pain and helped it build up enough
          strength to be released. Our team in Goa, India gave a Barn Owl back
          the gift of flight after he broke his wing. Following corrective
          surgery and 55 days of care and observation ‘Edward’ was able to be
          released.
        </Text>
        <Text>
          <strong>
            Your support means our veterinary teams around the world can make
            sure every animal receives the expert care it needs.
          </strong>
        </Text>
        <span id="working-together" />
      </Card>
    </Row>
    <Row className="mb-50">
      <Card image={data.cardImage5}>
        <H3>Working together for wildlife</H3>
        <Text>
          La Senda Verde (LSV), a sanctuary in Bolivia, provides refuge to over
          800 animals, innocent victims of illegal trafficking, cruelty and
          habitat destruction. Animals like Ajayu, an Andean bear, who was
          rescued when his mother was killed and he was badly beaten, leaving
          him blind. He had a wonderful home at the sanctuary, but because he
          was blind, local spider monkeys would come in his enclosure and steal
          his food. We were able to help LSV build a roof on his enclosure,
          allowing him to eat in peace and get some rest.
        </Text>
        <Text>
          <strong>
            With your support we can help more animals like Ajayu, and work with
            wonderful, life-changing organisations around the world, to make
            sure every animal receives the care they deserve.
          </strong>
        </Text>
        <Text>
          We have helped LSV expand their veterinary clinic, including a new
          X-ray room, six new rehabilitation enclosures and a new surgery, as
          well as arranging for them to receive a new X-ray machine.
        </Text>
        <Text>
          <strong>
            Donate today and you can help us continue to provide life-saving
            equipment and supplies to organisations that are saving wildlife
            around the world.
          </strong>
        </Text>
        <div className="d-flex flex-r mt-30">
          <LinkButton title="Donate" to="/support-us/wildlife-appeal/amount">
            Donate
          </LinkButton>
        </div>
      </Card>
    </Row>
    <Row className="mt-50 mb-20 text-center">
      <H2>What else can I do?</H2>
    </Row>
    <Row className="mb-50">
      <Card image={data.donateCardImage} imageFirst imageHeight="280px">
        <H3>Donate today</H3>
        <Text>
          Please make a real and lasting difference to an animal in need by
          donating today. Any amount, big or small, will mean so much to an
          animal with no other hope of care.
        </Text>
        <div className="d-flex flex-r mt-30">
          <LinkButton title="Donate" to="/support-us/wildlife-appeal/amount">
            Donate
          </LinkButton>
        </div>
      </Card>
    </Row>
  </Layout>
);

export default WildlifeAppealPage;
